import React from "react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";

const SolutionsPage: React.FC = () => {
  const solutions = [
    {
      title: "Tech",
      description:
        "Automate your software development workflows, DevOps pipelines, and technical operations.",
      gradient: "from-blue-600 via-cyan-500 to-teal-400",
      features: [
        "CI/CD pipeline automation",
        "Infrastructure as code",
        "Automated testing & deployment",
        "Code review automation",
      ],
    },
    {
      title: "Consulting",
      description:
        "Streamline client management, project workflows, and reporting processes.",
      gradient: "from-fuchsia-600 via-pink-500 to-orange-400",
      features: [
        "Client onboarding automation",
        "Project status reporting",
        "Document generation",
        "Time tracking & billing",
      ],
    },
    {
      title: "Strategy",
      description:
        "Leverage AI to analyze data, generate insights, and inform strategic decisions.",
      gradient: "from-amber-500 via-orange-500 to-red-500",
      features: [
        "Market research automation",
        "Competitive analysis",
        "Data-driven insights",
        "Strategic planning tools",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left lg:pl-8">
              <h1 className="text-5xl lg:text-6xl font-light leading-tight mb-6">
                Solutions built for{" "}
                <span className="text-gray-500">your industry.</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto lg:mx-0 leading-relaxed">
                We build custom AI automation tailored to your specific needs,
                whether you're in tech, consulting, or strategy.
              </p>
            </div>

            <div className="relative">
              <div className="rounded-3xl h-80 md:h-96 relative overflow-hidden bg-transparent flex items-center justify-center p-4">
                <img
                  src="/ChatGPT%20Image%20Nov%2025,%202025,%2008_22_21%20PM.png"
                  alt="Softly painted rose bloom"
                  className="max-h-full max-w-full object-contain drop-shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <div key={index} className="space-y-5">
                {/* Card */}
                <div className="h-20 flex items-center justify-center">
                  <div className="text-black text-3xl md:text-4xl font-light">
                    {solution.title}
                  </div>
                </div>

                {/* Content */}
                <div className="space-y-3">
                  <p className="text-gray-600 leading-relaxed">
                    {solution.description}
                  </p>

                  <ul className="space-y-2">
                    {solution.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <div className="w-5 h-5 bg-green-100 text-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs">
                          ✓
                        </div>
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-light leading-tight mb-6">
            Ready to automate your workflows?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Book a free consultation to discuss your specific needs.
          </p>
          <a
            href="https://calendly.com/rohansontakke/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gray-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors inline-block text-lg"
          >
            Book a free call →
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SolutionsPage;
